// Branding Page (Refactored)
// Original: 1147 lines → Refactored: ~400 lines
// Components extracted to /components/branding/

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  MessageSquare, 
  Palette, 
  Save, 
  Loader2, 
  LayoutDashboard,
  Settings,
  Link2,
  FileCode,
  LogOut,
  Check,
  Shield,
  FileText,
  MapPin,
  Download,
  HelpCircle,
  ClipboardList,
  Trash2,
  MailX,
  MessageSquareOff,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Import extracted components
import {
  LogoUpload,
  WelcomeVideoUpload,
  ColorSettings,
  WidgetSettings,
  EmailSettings,
  FirmContactInfo
} from '@/components/branding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// State disclaimer options by state
const STATE_DISCLAIMERS = {
  "NONE": "",
  "AL": "No representation is made that the quality of legal services to be performed is greater than the quality of legal services performed by other lawyers.",
  "FL": "The hiring of a lawyer is an important decision that should not be based solely upon advertisements.",
  "GA": "No representation is made that the quality of the legal services to be performed is greater than the quality of legal services performed by other lawyers.",
  "IL": "Advertising material.",
  "KY": "THIS IS AN ADVERTISEMENT.",
  "MS": "Free background information available upon request.",
  "MO": "Advertising material. Past results do not guarantee future outcomes.",
  "NJ": "Attorney advertisement. Prior results do not guarantee a similar outcome.",
  "NY": "Attorney Advertising. Prior results do not guarantee a similar outcome.",
  "TX": "Attorney advertising. Unless otherwise noted, attorneys are not certified by the Texas Board of Legal Specialization.",
  "DEFAULT": "Attorney Advertising. Prior results do not guarantee future outcomes."
};

const BrandingPage = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [downloadingDPA, setDownloadingDPA] = useState(false);
  
  const [branding, setBranding] = useState({
    logo_url: '',
    primary_color: '#0d9488',
    secondary_color: '#0f172a',
    widget_greeting: '',
    widget_title: '',
    chat_bubble_text: '',
    email_signature: '',
    firm_phone: '',
    firm_email: '',
    welcome_video_url: '',
    welcome_video_thumbnail: '',
    physical_address: '',
    state_disclaimer: '',
    require_sms_consent: true,
    show_cookie_banner: true,
    data_retention_days: 0,
    ai_disclaimer_text: ''
  });
  
  // Consent Audit Log state
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPagination, setAuditPagination] = useState({ total: 0, skip: 0, limit: 20 });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      // Auth via httpOnly cookie - no header needed
      const response = await axios.get(`${API}/branding`);
      setBranding(response.data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, branding);
      toast.success('Branding settings saved!');
    } catch {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBranding(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video file too large. Maximum size is 50MB.');
      return;
    }
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/branding/welcome-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBranding(prev => ({ ...prev, welcome_video_url: response.data.video_url }));
      toast.success('Welcome video uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/branding/welcome-video-thumbnail`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBranding(prev => ({ ...prev, welcome_video_thumbnail: response.data.thumbnail_url }));
      toast.success('Thumbnail uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleRemoveVideo = async () => {
    try {
      await axios.delete(`${API}/branding/welcome-video`);
      setBranding(prev => ({ ...prev, welcome_video_url: '', welcome_video_thumbnail: '' }));
      toast.success('Welcome video removed');
    } catch {
      toast.error('Failed to remove video');
    }
  };

  const fetchAuditLog = async (skip = 0) => {
    setAuditLoading(true);
    try {
      const response = await axios.get(`${API}/compliance/consent-log`, {
        params: { skip, limit: 20 }
      });
      setAuditLogs(response.data.logs);
      setAuditPagination({
        total: response.data.total,
        skip: response.data.skip,
        limit: response.data.limit
      });
    } catch {
      toast.error('Failed to load consent audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDownloadDPA = async () => {
    setDownloadingDPA(true);
    try {
      const response = await axios.get(`${API}/compliance/dpa-template`);
      
      const blob = new Blob([response.data.template], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Data_Processing_Agreement.txt';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('DPA template downloaded');
    } catch {
      toast.error('Failed to download DPA template');
    } finally {
      setDownloadingDPA(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'email_unsubscribe': return <MailX className="w-4 h-4 text-orange-500" />;
      case 'sms_optout': return <MessageSquareOff className="w-4 h-4 text-orange-500" />;
      case 'data_deletion': return <Trash2 className="w-4 h-4 text-red-500" />;
      default: return <ClipboardList className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatActionName = (action) => {
    const names = {
      'email_unsubscribe': 'Email Unsubscribe',
      'sms_optout': 'SMS Opt-Out',
      'data_deletion': 'Data Deletion',
      'consent_given': 'Consent Given'
    };
    return names[action] || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white p-6 flex flex-col fixed h-full">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>IntakePal</span>
        </div>

        <nav className="flex-1 space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            <LayoutDashboard className="w-5 h-5" /> Dashboard
          </Link>
          <Link to="/questions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            <Settings className="w-5 h-5" /> Questions
          </Link>
          <Link to="/integrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            <Link2 className="w-5 h-5" /> Integrations
          </Link>
          <Link to="/widget" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors">
            <FileCode className="w-5 h-5" /> Widget
          </Link>
          <Link to="/branding" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-teal-500/20 text-teal-400">
            <Palette className="w-5 h-5" /> Branding
          </Link>
        </nav>

        <div className="border-t border-slate-700 pt-4 mt-4">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.firm_name}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white w-full transition-colors" data-testid="logout-btn">
            <LogOut className="w-5 h-5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Palette className="w-7 h-7 text-teal-500" />
              Branding & Compliance Settings
            </h1>
            <p className="text-slate-600 mt-2">
              Customize how your intake widget and emails appear to clients.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <LogoUpload 
              logoUrl={branding.logo_url}
              uploading={uploadingLogo}
              onUpload={handleLogoUpload}
            />

            {/* Welcome Video */}
            <WelcomeVideoUpload
              videoUrl={branding.welcome_video_url}
              thumbnailUrl={branding.welcome_video_thumbnail}
              uploadingVideo={uploadingVideo}
              uploadingThumbnail={uploadingThumbnail}
              onVideoUpload={handleVideoUpload}
              onThumbnailUpload={handleThumbnailUpload}
              onRemoveVideo={handleRemoveVideo}
            />

            {/* Colors */}
            <ColorSettings
              primaryColor={branding.primary_color}
              secondaryColor={branding.secondary_color}
              onChange={handleFieldChange}
            />

            {/* Widget Settings */}
            <WidgetSettings
              widgetTitle={branding.widget_title}
              chatBubbleText={branding.chat_bubble_text}
              widgetGreeting={branding.widget_greeting}
              onChange={handleFieldChange}
            />

            {/* Email Settings */}
            <EmailSettings
              emailSignature={branding.email_signature}
              onChange={handleFieldChange}
            />

            {/* Firm Contact Info */}
            <FirmContactInfo
              firmPhone={branding.firm_phone}
              firmEmail={branding.firm_email}
              onChange={handleFieldChange}
            />
          </div>

          {/* Legal Compliance Section */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-teal-500" />
              Legal Compliance Settings
            </h2>
            <p className="text-slate-600 mb-6">
              Configure settings for TCPA, CCPA, CAN-SPAM, and state bar compliance requirements.
            </p>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* CAN-SPAM / Physical Address */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5 text-teal-500" />
                    CAN-SPAM Compliance
                  </CardTitle>
                  <CardDescription>
                    Federal law requires a physical mailing address in commercial emails
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label htmlFor="physical_address">Physical Address</Label>
                    <Textarea
                      id="physical_address"
                      value={branding.physical_address || ''}
                      onChange={(e) => handleFieldChange('physical_address', e.target.value)}
                      placeholder="123 Main St, Suite 100&#10;City, State 12345"
                      className="mt-1"
                      rows={3}
                      data-testid="physical-address-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      This address appears at the bottom of all follow-up emails
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* State Bar Disclaimer */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-teal-500" />
                    State Bar Disclaimer
                  </CardTitle>
                  <CardDescription>
                    Required attorney advertising disclaimers vary by state
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Select Your State</Label>
                      <Select
                        value={Object.keys(STATE_DISCLAIMERS).find(key => STATE_DISCLAIMERS[key] === branding.state_disclaimer) || 'DEFAULT'}
                        onValueChange={(value) => handleFieldChange('state_disclaimer', STATE_DISCLAIMERS[value])}
                      >
                        <SelectTrigger className="mt-1" data-testid="state-select">
                          <SelectValue placeholder="Choose state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">No Disclaimer</SelectItem>
                          <SelectItem value="DEFAULT">Default (Generic)</SelectItem>
                          <SelectItem value="AL">Alabama</SelectItem>
                          <SelectItem value="FL">Florida</SelectItem>
                          <SelectItem value="GA">Georgia</SelectItem>
                          <SelectItem value="IL">Illinois</SelectItem>
                          <SelectItem value="KY">Kentucky</SelectItem>
                          <SelectItem value="MS">Mississippi</SelectItem>
                          <SelectItem value="MO">Missouri</SelectItem>
                          <SelectItem value="NJ">New Jersey</SelectItem>
                          <SelectItem value="NY">New York</SelectItem>
                          <SelectItem value="TX">Texas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {branding.state_disclaimer && (
                      <div className="p-3 bg-slate-50 rounded-lg border text-sm text-slate-600">
                        {branding.state_disclaimer}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* TCPA Settings */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MessageSquare className="w-5 h-5 text-teal-500" />
                    TCPA Compliance
                  </CardTitle>
                  <CardDescription>
                    Telephone Consumer Protection Act requirements for SMS
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Require SMS Consent</Label>
                      <p className="text-xs text-slate-500">Show SMS opt-in checkbox in intake form</p>
                    </div>
                    <Switch
                      checked={branding.require_sms_consent}
                      onCheckedChange={(checked) => handleFieldChange('require_sms_consent', checked)}
                      data-testid="sms-consent-switch"
                    />
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800">
                    <strong>Note:</strong> TCPA requires express written consent before sending marketing SMS. We recommend keeping this enabled.
                  </div>
                </CardContent>
              </Card>

              {/* CCPA/GDPR Settings */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="w-5 h-5 text-teal-500" />
                    Privacy & Data Protection
                  </CardTitle>
                  <CardDescription>
                    CCPA, GDPR, and privacy compliance settings
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Cookie Consent Banner</Label>
                      <p className="text-xs text-slate-500">Show cookie notice in chat widget</p>
                    </div>
                    <Switch
                      checked={branding.show_cookie_banner}
                      onCheckedChange={(checked) => handleFieldChange('show_cookie_banner', checked)}
                      data-testid="cookie-banner-switch"
                    />
                  </div>

                  <div>
                    <Label>Data Retention Period</Label>
                    <Select
                      value={String(branding.data_retention_days || 0)}
                      onValueChange={(value) => handleFieldChange('data_retention_days', parseInt(value))}
                    >
                      <SelectTrigger className="mt-1" data-testid="data-retention-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Indefinite</SelectItem>
                        <SelectItem value="365">1 Year</SelectItem>
                        <SelectItem value="730">2 Years</SelectItem>
                        <SelectItem value="1095">3 Years</SelectItem>
                        <SelectItem value="1825">5 Years</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Auto-delete client data after this period</p>
                  </div>

                  <div className="pt-4 border-t">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleDownloadDPA}
                      disabled={downloadingDPA}
                      data-testid="download-dpa-btn"
                    >
                      {downloadingDPA ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download DPA Template
                    </Button>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Data Processing Agreement for vendor compliance
                    </p>
                  </div>

                  <div className="pt-4 border-t">
                    <Dialog open={showAuditLog} onOpenChange={(open) => {
                      setShowAuditLog(open);
                      if (open) fetchAuditLog(0);
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" data-testid="view-audit-log-btn">
                          <ClipboardList className="w-4 h-4 mr-2" />
                          View Consent Audit Log
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-teal-500" />
                            Consent Audit Log
                          </DialogTitle>
                          <DialogDescription>
                            Track all consent-related events for compliance documentation
                          </DialogDescription>
                        </DialogHeader>
                        
                        <div className="flex-1 overflow-auto">
                          {auditLoading ? (
                            <div className="flex items-center justify-center py-12">
                              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
                            </div>
                          ) : auditLogs.length === 0 ? (
                            <div className="text-center py-12">
                              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                              <p className="text-slate-500">No consent events recorded yet</p>
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-[140px]">Timestamp</TableHead>
                                  <TableHead className="w-[130px]">Action</TableHead>
                                  <TableHead>Client/Email</TableHead>
                                  <TableHead className="w-[150px]">Performed By</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {auditLogs.map((log) => (
                                  <TableRow key={log.id}>
                                    <TableCell className="text-xs text-slate-500">
                                      {new Date(log.timestamp).toLocaleDateString()}<br/>
                                      {new Date(log.timestamp).toLocaleTimeString()}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {getActionIcon(log.action)}
                                        <span className="text-sm font-medium">
                                          {formatActionName(log.action)}
                                        </span>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="text-sm">
                                        {log.client_name && <p className="font-medium">{log.client_name}</p>}
                                        <p className="text-slate-500 text-xs">{log.email || log.phone || '-'}</p>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-xs text-slate-500">
                                      {log.deleted_by || 'Client'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                        
                        {auditPagination.total > auditPagination.limit && (
                          <div className="flex items-center justify-between pt-4 border-t">
                            <p className="text-sm text-slate-500">
                              Showing {auditPagination.skip + 1} - {Math.min(auditPagination.skip + auditPagination.limit, auditPagination.total)} of {auditPagination.total}
                            </p>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchAuditLog(Math.max(0, auditPagination.skip - auditPagination.limit))}
                                disabled={auditPagination.skip === 0 || auditLoading}
                              >
                                <ChevronLeft className="w-4 h-4" /> Previous
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchAuditLog(auditPagination.skip + auditPagination.limit)}
                                disabled={auditPagination.skip + auditPagination.limit >= auditPagination.total || auditLoading}
                              >
                                Next <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      Compliance documentation for state bar auditors
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-500 hover:bg-teal-600 px-8"
              data-testid="save-branding-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BrandingPage;
